import {keyToCoords} from "../../coords/keyToCoords";
import {HexMath} from "../../HexMath";
import {SUCCUBES_SEDUCTION_RANGE} from "../../../configs/factions/succubes";
import {hasAdjacentEnemyUnit} from "../../gameState/hasAdjacentEnemyUnit";
import {isUnitFaction} from "../isUnitFaction";
import {FACTIONS} from "../../../constants/factions";
import {hasObstacleInPath} from "../../gameState/shooting/calculateAvailableShootingCells";
import UNIT_TYPES from "../../../configs/units";

/**
 * Рассчитывает доступные для соблазнения ячейки с юнитами, не принадлежащими
 * текущему владельцу выбранного юнита суккубов: вражеские, нейтральные, либо
 * ранее захваченные другим игроком-суккубом. Дальность и прямая видимость —
 * один в один с обнаружением целей у стреляющих отрядов эльфов
 * (calculateAvailableShootingCellsForElves): гекс-дистанция ≤ SUCCUBES_SEDUCTION_RANGE
 * И отсутствие препятствия (гора/непобеждённая крепость) на прямой линии между отрядами.
 * Соблазнение "сквозь стены" была слишком сильной способностью для механики,
 * означающей обольщение конкретного отряда, а не магию на расстоянии
 * @param {Object} hexes - состояние гексов
 * @param {Array} units - все отряды
 * @param {Object} selectedUnit - выбранный юнит суккубов
 * @param {Object} specialBuildings - специальные строения
 * @param {Object} buildingsDamage - уже применённые повреждения строений
 * @param {Array} fortressesUnderConstruction - крепости в строительстве
 * @returns {Array} список hexKey доступных целей
 */
export const calculateAvailableSeductionCells = (
  hexes,
  units,
  selectedUnit,
  specialBuildings,
  buildingsDamage,
  fortressesUnderConstruction,
) => {
  // Юнит, уже пытавшийся соблазнить в этом этапе, не имеет доступных целей -
  // без этой проверки подсветка целей (HexGridRenderer.updateSeductionMarkers)
  // и обработка клика (GameScene.handleAbilitiesPhaseClick) считали бы такого
  // юнита по-прежнему способным атаковать, т.к. оба вызывают эту функцию
  // напрямую, в обход checkIfSuccubesUnitCouldSeduce
  if (!selectedUnit || selectedUnit.hasUsedAbilityThisWarPhase) {
    return [];
  }

  // Способность фракционная: захваченный отряд чужой фракции (unit.factionId
  // !== FACTIONS.SUCCUBES) не должен считаться способным соблазнять только
  // потому, что временно принадлежит игроку-суккубу (см. design.md Decision 16
  // - единая точка правды для доступности этого действия)
  if (!isUnitFaction(selectedUnit, FACTIONS.SUCCUBES)) {
    return [];
  }

  // Катапульты (D4) не соблазняют - иначе на этапе способностей у катапульты
  // суккубов конфликтовали бы две зоны действия: по радиусу соблазнения
  // (SUCCUBES_SEDUCTION_RANGE) и по радиусу стрельбы (5, зона стрельбы работает
  // вне зависимости от этапа, см. HexGridRenderer.resolveRangeZoneSource). См. design.md
  if (selectedUnit.type === UNIT_TYPES.D4.id) {
    return [];
  }

  // Отряд, зажатый вплотную вражеским отрядом, не может применять способности
  // на этапе способностей - правило общее для всех способностей этого этапа
  // (по аналогии с запретом стрельбы для зажатого стрелка)
  if (hasAdjacentEnemyUnit(selectedUnit.hexKey, selectedUnit.ownerId, units)) {
    return [];
  }

  const hexMath = new HexMath(30, 'flat');
  const [selectedQ, selectedR] = keyToCoords(selectedUnit.hexKey);
  const seducerOwnerId = selectedUnit.ownerId;

  const targetCells = [];

  units.forEach(unit => {
    // Нельзя соблазнить юнит, уже принадлежащий текущему владельцу
    if (unit.ownerId === seducerOwnerId) {
      return;
    }

    const [targetQ, targetR] = keyToCoords(unit.hexKey);
    const distance = hexMath.hexDistance(selectedQ, selectedR, targetQ, targetR);

    if (distance > SUCCUBES_SEDUCTION_RANGE) {
      return;
    }

    // Соблазнение — не бросок снаряда: у стрельбы pendingShootingActions
    // учитывается для того, чтобы крепость, которую УЖЕ ДОБЬЮТ в этом же
    // этапе, не считалась препятствием; на этапе способностей стрельбы ещё не
    // было (ABILITIES идёт раньше SHOOTING, см. design.md Decision 1), поэтому
    // здесь всегда пустой массив - учитывается только уже нанесённый урон
    if (hasObstacleInPath(selectedUnit.hexKey, unit.hexKey, hexes, specialBuildings, [], buildingsDamage, fortressesUnderConstruction)) {
      return;
    }

    targetCells.push(unit.hexKey);
  });

  return targetCells;
};
